import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { ordinalAlphabeticBy } from '@app/utils/ordinal'

export const useBubbleStore = defineStore('bubble', () => {
  const highlightedPath = ref('')
  // By keeping track of the bubbles per section, we can link them to one another by path
  // and derive useful reactive properties out of this linking relationship
  const inputBubblesPerPath = ref<Record<string, boolean>>({})
  const inputBubblesOrder = ref<string[]>([])
  const remarkBubblesPerPath = ref<Record<string, { id: string }>>({})

  function registerBubble(type: 'input', params: { linkingPath: string }): void
  function registerBubble(
    type: 'remark',
    params: { id: string; linkingPath: string }
  ): void

  function registerBubble(
    type: 'input' | 'remark',
    {
      id,
      linkingPath,
    }: {
      id?: string
      linkingPath: string
    }
  ): void {
    if (type === 'input') {
      inputBubblesPerPath.value[linkingPath] = true

      if (!inputBubblesOrder.value.includes(linkingPath)) {
        inputBubblesOrder.value.push(linkingPath)
      }
    } else {
      remarkBubblesPerPath.value[linkingPath] = { id: id ?? '' }
    }
  }

  function unregisterBubble(
    type: 'input' | 'remark',
    linkingPath: string
  ): void {
    if (type === 'input') {
      delete inputBubblesPerPath.value[linkingPath]

      const position = inputBubblesOrder.value.indexOf(linkingPath)

      if (position !== -1) {
        inputBubblesOrder.value.splice(position, 1)
      }
    } else {
      delete remarkBubblesPerPath.value[linkingPath]
    }
  }

  const ordinal = computed(() => {
    const letters: Record<string, string> = {}
    let position = 0

    const assign = (path: string): void => {
      if (letters[path]) {
        return
      }

      position += 1
      letters[path] = ordinalAlphabeticBy(position)
    }

    // Input bubbles register in document order, so a pair reads A, B, C down
    // the template.
    inputBubblesOrder.value.forEach((path) => {
      if (remarkBubblesPerPath.value[path]) {
        assign(path)
      }
    })

    // A note with nothing to point at (a deleted fori row, an input type that
    // carries no bubble, a PDF annotation) takes what is left, so it never
    // holds a letter that belongs to an input on the page.
    Object.keys(remarkBubblesPerPath.value).forEach(assign)

    return letters
  })

  function setHighlightedBubble(path: string): void {
    highlightedPath.value = path
  }

  return {
    inputBubblesPerPath,
    remarkBubblesPerPath,
    highlightedPath,
    registerBubble,
    unregisterBubble,
    ordinal,
    setHighlightedBubble,
  }
})
